import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getOriginColor } from "../utils/layerTree";
import ZoomControls from "./ZoomControls";
import { useCanvasZoom } from "./useCanvasZoom";

const objectFitFromBox = (fit) => (fit === "cover" ? "cover" : "contain");
const MIN_SIZE = 0.02;
const HANDLE_SIZE = 10;
const LABEL_HEIGHT = 18;
const VIEWPORT_PADDING = 24;
const RESIZE_HANDLES = [
  { corner: "nw", cursor: "nwse-resize" },
  { corner: "ne", cursor: "nesw-resize" },
  { corner: "sw", cursor: "nesw-resize" },
  { corner: "se", cursor: "nwse-resize" },
];

const clamp = ({ value, min, max }) => Math.min(max, Math.max(min, value));

const keepInsideStart = ({ start, size, offsetPx }) => {
  const room = size > 0 ? start / size : 0;
  return `max(-${offsetPx}px, -${room * 100}%)`;
};

const keepInsideEnd = ({ start, size, offsetPx }) => {
  const room = size > 0 ? (1 - start - size) / size : 0;
  return `max(-${offsetPx}px, -${room * 100}%)`;
};

const boxContains = ({ box, point }) =>
  point.x >= box.nx &&
  point.x <= box.nx + box.nw &&
  point.y >= box.ny &&
  point.y <= box.ny + box.nh;

const pickBoxAtPoint = ({ boxes, point, selectedID }) => {
  const selected = selectedID
    ? boxes.find((box) => box.id === selectedID)
    : null;
  if (selected && boxContains({ box: selected, point })) {
    return selected;
  }
  let best = null;
  let bestStack = -Infinity;
  boxes.forEach((box) => {
    if (!boxContains({ box, point })) return;
    const stack = stackZ({ box, selected: false });
    if (stack >= bestStack) {
      best = box;
      bestStack = stack;
    }
  });
  return best;
};

const stackZ = ({ box, selected }) => {
  const paint = Number(box.paintIndex) || 0;
  const z = Number(box.zIndex) || 0;
  const area = box.nw * box.nh;
  const areaTie = Math.round((1 - area) * 100);
  return paint * 100000 + z * 1000 + areaTie + (selected ? 1 : 0);
};

const handlePositionSx = ({ box, corner }) => {
  const offsetPx = HANDLE_SIZE / 2;
  const sx = {};
  if (corner.includes("n")) {
    sx.top = keepInsideStart({
      start: box.ny,
      size: box.nh,
      offsetPx,
    });
  } else {
    sx.bottom = keepInsideEnd({
      start: box.ny,
      size: box.nh,
      offsetPx,
    });
  }
  if (corner.includes("w")) {
    sx.left = keepInsideStart({
      start: box.nx,
      size: box.nw,
      offsetPx,
    });
  } else {
    sx.right = keepInsideEnd({
      start: box.nx,
      size: box.nw,
      offsetPx,
    });
  }
  return sx;
};

const resizeFromCorner = ({ drag, point }) => {
  const dx = point.x - drag.startX;
  const dy = point.y - drag.startY;
  const originRight = drag.originNx + drag.originNw;
  const originBottom = drag.originNy + drag.originNh;
  let nx = drag.originNx;
  let ny = drag.originNy;
  let nw = drag.originNw;
  let nh = drag.originNh;

  if (drag.corner.includes("w")) {
    nx = clamp({
      value: drag.originNx + dx,
      min: 0,
      max: originRight - MIN_SIZE,
    });
    nw = originRight - nx;
  } else if (drag.corner.includes("e")) {
    nw = clamp({
      value: drag.originNw + dx,
      min: MIN_SIZE,
      max: 1 - drag.originNx,
    });
  }

  if (drag.corner.includes("n")) {
    ny = clamp({
      value: drag.originNy + dy,
      min: 0,
      max: originBottom - MIN_SIZE,
    });
    nh = originBottom - ny;
  } else if (drag.corner.includes("s")) {
    nh = clamp({
      value: drag.originNh + dy,
      min: MIN_SIZE,
      max: 1 - drag.originNy,
    });
  }

  return { nx, ny, nw, nh };
};

const TemplateCanvasOverlay = ({
  imageUrl,
  showGuide = true,
  artboardWidth,
  artboardHeight,
  boxes = [],
  selectedID,
  isDrawing,
  isLocked,
  isScanning = false,
  analysisError = "",
  retryLabel = "",
  onRetry,
  onSelectBox,
  onCreateBox,
  onUpdateBox,
  onEndBoxTransform,
}) => {
  const boardRef = useRef(null);
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const { zoom, viewportSize, onZoomIn, onZoomOut, onFit } = useCanvasZoom({
    viewportRef,
    boardRef,
    resetKey: `${artboardWidth}x${artboardHeight}`,
    lockRef: dragRef,
  });

  const getPoint = ({ event }) => {
    const rect = boardRef.current.getBoundingClientRect();
    return {
      x: clamp({
        value: (event.clientX - rect.left) / rect.width,
        min: 0,
        max: 1,
      }),
      y: clamp({
        value: (event.clientY - rect.top) / rect.height,
        min: 0,
        max: 1,
      }),
    };
  };

  const onPointerDownBoard = useCallback(
    (event) => {
      if (isLocked) return;
      const point = getPoint({ event });
      if (isDrawing) {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDraft({
          x: point.x,
          y: point.y,
          w: 0,
          h: 0,
          originX: point.x,
          originY: point.y,
        });
        return;
      }
      const box = pickBoxAtPoint({ boxes, point, selectedID });
      if (!box) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      onSelectBox(box.id);
      if (box.isBackground) return;
      dragRef.current = {
        mode: "move",
        id: box.id,
        startX: point.x,
        startY: point.y,
        originNx: box.nx,
        originNy: box.ny,
        originNw: box.nw,
        originNh: box.nh,
      };
    },
    [boxes, isDrawing, isLocked, onSelectBox, selectedID]
  );

  const onPointerMoveBoard = useCallback(
    (event) => {
      if (!draft) return;
      const point = getPoint({ event });
      const x = Math.min(draft.originX, point.x);
      const y = Math.min(draft.originY, point.y);
      setDraft({
        ...draft,
        x,
        y,
        w: Math.abs(point.x - draft.originX),
        h: Math.abs(point.y - draft.originY),
      });
    },
    [draft]
  );

  const onPointerUpBoard = useCallback(() => {
    if (!draft) return;
    if (draft.w >= MIN_SIZE && draft.h >= MIN_SIZE) {
      onCreateBox({ nx: draft.x, ny: draft.y, nw: draft.w, nh: draft.h });
    }
    setDraft(null);
  }, [draft, onCreateBox]);

  const onPointerDownResize = ({ event, box, corner }) => {
    if (isLocked || isDrawing || box.isBackground) return;
    event.stopPropagation();
    onSelectBox(box.id);
    const point = getPoint({ event });
    dragRef.current = {
      mode: "resize",
      corner,
      id: box.id,
      startX: point.x,
      startY: point.y,
      originNx: box.nx,
      originNy: box.ny,
      originNw: box.nw,
      originNh: box.nh,
    };
  };

  const onPointerMoveWindow = useCallback(
    (event) => {
      if (!dragRef.current) return;
      const point = getPoint({ event });
      const drag = dragRef.current;
      const box = boxes.find((item) => item.id === drag.id);
      if (!box) return;
      const dx = point.x - drag.startX;
      const dy = point.y - drag.startY;
      if (drag.mode === "resize") {
        onUpdateBox({
          id: box.id,
          ...resizeFromCorner({ drag, point }),
        });
        return;
      }
      onUpdateBox({
        id: box.id,
        nx: clamp({
          value: drag.originNx + dx,
          min: 0,
          max: 1 - box.nw,
        }),
        ny: clamp({
          value: drag.originNy + dy,
          min: 0,
          max: 1 - box.nh,
        }),
      });
    },
    [boxes, onUpdateBox]
  );

  const onPointerUpWindow = useCallback(() => {
    dragRef.current = null;
    onEndBoxTransform?.();
  }, [onEndBoxTransform]);

  const aspect =
    artboardWidth && artboardHeight ? artboardWidth / artboardHeight : 1;
  const { fitWidth, fitHeight } = useMemo(() => {
    const pad = VIEWPORT_PADDING * 2;
    const availableWidth = Math.max(1, viewportSize.width - pad);
    const availableHeight = Math.max(1, viewportSize.height - pad);
    if (availableWidth / availableHeight > aspect) {
      return {
        fitHeight: availableHeight,
        fitWidth: availableHeight * aspect,
      };
    }
    return {
      fitWidth: availableWidth,
      fitHeight: availableWidth / aspect,
    };
  }, [aspect, viewportSize.height, viewportSize.width]);
  const boardWidth = fitWidth * zoom;
  const boardHeight = fitHeight * zoom;

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        ref={viewportRef}
        sx={{
          height: "60vh",
          minHeight: 360,
          overflow: "auto",
          display: "flex",
          bgcolor: "#F4F4F5",
          border: "1px solid #E4E4E7",
          borderRadius: "12px",
          overscrollBehavior: "contain",
        }}
      >
        <Box
          sx={{
            margin: "auto",
            p: `${VIEWPORT_PADDING}px`,
            flexShrink: 0,
          }}
        >
          <Box
            ref={boardRef}
            onPointerDown={onPointerDownBoard}
            onPointerMove={(event) => {
              onPointerMoveBoard(event);
              onPointerMoveWindow(event);
            }}
            onPointerUp={() => {
              onPointerUpBoard();
              onPointerUpWindow();
            }}
            sx={{
              position: "relative",
              isolation: "isolate",
              width: boardWidth,
              height: boardHeight,
              borderRadius: 0,
              overflow: "hidden",
              border: "1px solid #E4E4E7",
              backgroundColor: "#EEE",
              backgroundImage: showGuide && imageUrl ? `url(${imageUrl})` : "none",
              backgroundSize: "100% 100%",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              cursor: isLocked ? "not-allowed" : isDrawing ? "crosshair" : "move",
              userSelect: "none",
              touchAction: "none",
            }}
          >
            {boxes.map((box) => {
              const color = getOriginColor({
                sourceKind: box.sourceKind,
                type: box.type,
              });
              const selected = selectedID === box.id;
              const showAsset = !showGuide && Boolean(box.assetUrl);
              return (
                <Box
                  key={box.id}
                  sx={{
                    position: "absolute",
                    left: `${box.nx * 100}%`,
                    top: `${box.ny * 100}%`,
                    width: `${box.nw * 100}%`,
                    height: `${box.nh * 100}%`,
                    zIndex: stackZ({ box, selected }),
                    border: selected ? `2px solid ${color}` : `1.5px solid ${color}`,
                    backgroundColor: showAsset ? "transparent" : `${color}1A`,
                    boxShadow: selected ? "0 0 0 3px rgba(24,24,27,.2)" : "none",
                    pointerEvents: "none",
                  }}
                >
                  {showAsset ? (
                    <Box
                      component="img"
                      src={box.assetUrl}
                      alt=""
                      sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: objectFitFromBox(box.fit),
                        pointerEvents: "none",
                        userSelect: "none",
                        zIndex: 0,
                      }}
                    />
                  ) : null}
                  <Typography
                    sx={{
                      position: "absolute",
                      top: keepInsideStart({
                        start: box.ny,
                        size: box.nh,
                        offsetPx: LABEL_HEIGHT,
                      }),
                      left: 0,
                      zIndex: 1,
                      fontSize: "10.5px",
                      lineHeight: `${LABEL_HEIGHT}px`,
                      px: 0.75,
                      borderRadius: "5px",
                      bgcolor: color,
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {box.name}
                  </Typography>
                  {selected && !isLocked && !isDrawing && !box.isBackground
                    ? RESIZE_HANDLES.map((handle) => (
                        <Box
                          key={handle.corner}
                          onPointerDown={(event) =>
                            onPointerDownResize({
                              event,
                              box,
                              corner: handle.corner,
                            })
                          }
                          sx={{
                            position: "absolute",
                            ...handlePositionSx({ box, corner: handle.corner }),
                            width: HANDLE_SIZE,
                            height: HANDLE_SIZE,
                            borderRadius: "2px",
                            bgcolor: color,
                            border: "1px solid #fff",
                            cursor: handle.cursor,
                            pointerEvents: "auto",
                            zIndex: 2,
                          }}
                        />
                      ))
                    : null}
                </Box>
              );
            })}
            {draft ? (
              <Box
                sx={{
                  position: "absolute",
                  left: `${draft.x * 100}%`,
                  top: `${draft.y * 100}%`,
                  width: `${draft.w * 100}%`,
                  height: `${draft.h * 100}%`,
                  border: "1.5px dashed #18181B",
                  backgroundColor: "rgba(24,24,27,.08)",
                  pointerEvents: "none",
                }}
              />
            ) : null}
            {isScanning ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                  zIndex: 20,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    width: "30%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                    animation: "templateScanSweep 2.2s ease-in-out infinite",
                    "@keyframes templateScanSweep": {
                      "0%": { transform: "translateX(-120%)" },
                      "100%": { transform: "translateX(430%)" },
                    },
                  }}
                />
              </Box>
            ) : null}
            {analysisError ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 21,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  bgcolor: "rgba(15,15,15,0.55)",
                  px: 2,
                }}
              >
                <Typography
                  sx={{ color: "#fff", textAlign: "center", fontWeight: 500 }}
                >
                  {analysisError}
                </Typography>
                {onRetry ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={onRetry}
                    sx={{
                      border: 0,
                      borderRadius: "10px",
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      bgcolor: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    {retryLabel}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
      <ZoomControls
        zoom={zoom}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onFit={onFit}
      />
    </Box>
  );
};

export default TemplateCanvasOverlay;
